import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Settings2, Zap } from "lucide-react";
import { ParamNumberSliderField } from "@/components/alpha-quant/ParamNumberSliderField";

type StrategyParameterRow = {
  id: string;
  param_name: string;
  param_value: number;
  category: string | null;
  description: string | null;
  created_at: string;
  updated_at: string;
};

type BacktestResultRow = {
  id: string;
  strategy_name: string;
  period_start: string | null;
  period_end: string | null;
  config_json: any;
  metrics_json: any;
  trades_json: any;
  equity_curve_json: any;
  created_at: string;
};

const STRATEGY_NAME = "alpha_quant";

function inferFieldSpec(paramName: string) {
  const name = paramName.toLowerCase();

  if (name.startsWith("enable_")) {
    return { kind: "toggle" as const };
  }

  // Risk management
  if (name.startsWith("sl_") || name.startsWith("tp_")) {
    return { kind: "number" as const, min: 0, max: 50, step: 0.5, unit: "%" };
  }

  // Funding bps
  if (name.includes("fund") || name.includes("bps")) {
    return { kind: "number" as const, min: -500, max: 500, step: 1, unit: "bps" };
  }

  // Hours
  if (name.includes("hours")) {
    return { kind: "number" as const, min: 0, max: 168, step: 1, unit: "h" };
  }

  // Typical percent thresholds
  if (name.includes("oi_") || name.includes("px_") || name.includes("diff")) {
    return { kind: "number" as const, min: -50, max: 50, step: 0.1, unit: "%" };
  }

  return { kind: "number" as const, min: -100, max: 100, step: 0.1 };
}

function titleFromParamName(paramName: string) {
  return paramName
    .replace(/_/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

export default function AlphaQuantStrategyConfig() {
  const { toast } = useToast();

  const [loadingParams, setLoadingParams] = useState(true);
  const [saving, setSaving] = useState(false);
  const [processing, setProcessing] = useState(false);

  const [rows, setRows] = useState<StrategyParameterRow[]>([]);
  const [values, setValues] = useState<Record<string, number>>({});
  const [lastResult, setLastResult] = useState<BacktestResultRow | null>(null);

  const grouped = useMemo(() => {
    const byCategory = new Map<string, StrategyParameterRow[]>();
    for (const r of rows) {
      const cat = r.category ?? "outros";
      if (!byCategory.has(cat)) byCategory.set(cat, []);
      byCategory.get(cat)!.push(r);
    }
    // Stable ordering
    return Array.from(byCategory.entries()).map(([category, items]) => ({
      category,
      items: items.slice().sort((a, b) => a.param_name.localeCompare(b.param_name)),
    }));
  }, [rows]);

  const riskParams = useMemo(() => {
    const sl = rows.filter((r) => r.param_name.toLowerCase().startsWith("sl_"));
    const tp = rows.filter((r) => r.param_name.toLowerCase().startsWith("tp_"));
    const order = (a: StrategyParameterRow, b: StrategyParameterRow) => a.param_name.localeCompare(b.param_name);
    return {
      sl: sl.slice().sort(order),
      tp: tp.slice().sort(order),
    };
  }, [rows]);

  // Load parameters + last backtest
  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoadingParams(true);
      try {
        const { data, error } = await supabase
          .from("strategy_parameters")
          .select("*")
          .order("category", { ascending: true })
          .order("param_name", { ascending: true });

        if (error) throw error;
        const list = (data || []) as unknown as StrategyParameterRow[];

        if (cancelled) return;
        setRows(list);
        setValues(Object.fromEntries(list.map((r) => [r.param_name, Number(r.param_value)])));
      } catch (e: any) {
        if (!cancelled) {
          toast({
            title: "Falha ao carregar parâmetros",
            description: e?.message ?? "Erro desconhecido",
            variant: "destructive",
          });
        }
      } finally {
        if (!cancelled) setLoadingParams(false);
      }

      try {
        const { data, error } = await supabase
          .from("backtest_results")
          .select("*")
          .eq("strategy_name", STRATEGY_NAME)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) throw error;
        if (!cancelled) setLastResult((data as any) ?? null);
      } catch {
        // ok: no results yet
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [toast]);

  // Realtime: stop processing when a new result comes in
  useEffect(() => {
    const channel = supabase
      .channel("alpha-quant-backtest-results")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "backtest_results" },
        (payload) => {
          const next = payload.new as any as BacktestResultRow;
          if (!next?.strategy_name || next.strategy_name !== STRATEGY_NAME) return;
          setLastResult(next);
          setProcessing(false);
          toast({ title: "Backtest atualizado", description: "Resultado recebido." });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [toast]);

  const handleSaveAndRun = async () => {
    setSaving(true);
    setProcessing(true);
    try {
      const payload = rows.map((r) => ({
        param_name: r.param_name,
        param_value: Number(values[r.param_name] ?? r.param_value),
        category: r.category,
        description: r.description,
      }));

      const { error: upsertError } = await supabase
        .from("strategy_parameters")
        .upsert(payload, { onConflict: "param_name" });

      if (upsertError) throw upsertError;

      const { data, error: fnError } = await supabase.functions.invoke("quant-engine", {
        body: { action: "run_backtest", strategy_name: STRATEGY_NAME },
      });

      if (fnError) throw fnError;

      toast({
        title: "Parâmetros salvos",
        description: "Backtest disparado no quant-engine.",
      });

      // If the function returns an immediate result snapshot, we can show it.
      if (data?.metrics_json) {
        setLastResult(data as any);
      }
    } catch (e: any) {
      setProcessing(false);
      toast({
        title: "Erro ao salvar/rodar",
        description: e?.message ?? "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const pnl = useMemo(() => {
    const m = lastResult?.metrics_json;
    if (!m) return null;
    return (
      m.pnl ??
      m.total_pnl ??
      m.net_profit ??
      m.totalReturn ??
      m.total_return ??
      null
    );
  }, [lastResult]);

  return (
    <div className="container mx-auto space-y-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Settings2 className="h-5 w-5 text-muted-foreground" />
            <h1 className="text-2xl font-bold">Configuração de Estratégia (Alpha Quant)</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            Edita parâmetros no Supabase (schema vertical) e dispara o backtest via <span className="font-medium">quant-engine</span>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline">
            <Link to="/app/alpha-quant">Voltar ao Terminal</Link>
          </Button>
          <Button onClick={handleSaveAndRun} disabled={loadingParams || saving}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Zap className="h-4 w-4" />
                Salvar e Rodar Backtest
              </>
            )}
          </Button>
        </div>
      </header>

      {processing ? (
        <Card>
          <CardContent className="py-6 flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin" />
            <div>
              <p className="font-medium">Processando backtest…</p>
              <p className="text-sm text-muted-foreground">Aguardando atualização de backtest_results (Realtime).</p>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Resultado do Backtest</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!lastResult ? (
            <p className="text-sm text-muted-foreground">Nenhum resultado encontrado ainda para {STRATEGY_NAME}.</p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline">{lastResult.strategy_name}</Badge>
                {pnl != null ? <Badge variant="secondary">PnL: {String(pnl)}</Badge> : null}
                <Badge variant="outline">{new Date(lastResult.created_at).toLocaleString()}</Badge>
              </div>
              <Separator />
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <Card className="bg-card/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Métricas</CardTitle>
                  </CardHeader>
                  <CardContent className="text-xs text-muted-foreground whitespace-pre-wrap">
                    {JSON.stringify(lastResult.metrics_json ?? {}, null, 2)}
                  </CardContent>
                </Card>
                <Card className="bg-card/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Trades</CardTitle>
                  </CardHeader>
                  <CardContent className="text-xs text-muted-foreground whitespace-pre-wrap max-h-80 overflow-auto">
                    {JSON.stringify(lastResult.trades_json ?? [], null, 2)}
                  </CardContent>
                </Card>
                <Card className="bg-card/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Config (snapshot)</CardTitle>
                  </CardHeader>
                  <CardContent className="text-xs text-muted-foreground whitespace-pre-wrap max-h-80 overflow-auto">
                    {JSON.stringify(lastResult.config_json ?? {}, null, 2)}
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Gestão de Risco (SL / TP por sinal)</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h3 className="text-sm font-semibold">Stop Loss</h3>
            {riskParams.sl.map((r) => {
              const spec = inferFieldSpec(r.param_name);
              if (spec.kind !== "number") return null;
              return (
                <ParamNumberSliderField
                  key={r.param_name}
                  id={`param-${r.param_name}`}
                  label={r.description || titleFromParamName(r.param_name)}
                  description={r.param_name}
                  value={Number(values[r.param_name] ?? r.param_value)}
                  onChange={(next) => setValues((p) => ({ ...p, [r.param_name]: next }))}
                  min={spec.min}
                  max={spec.max}
                  step={spec.step}
                  unit={spec.unit}
                  disabled={loadingParams}
                />
              );
            })}
          </div>

          <div className="space-y-4">
            <h3 className="text-sm font-semibold">Take Profit</h3>
            {riskParams.tp.map((r) => {
              const spec = inferFieldSpec(r.param_name);
              if (spec.kind !== "number") return null;
              return (
                <ParamNumberSliderField
                  key={r.param_name}
                  id={`param-${r.param_name}`}
                  label={r.description || titleFromParamName(r.param_name)}
                  description={r.param_name}
                  value={Number(values[r.param_name] ?? r.param_value)}
                  onChange={(next) => setValues((p) => ({ ...p, [r.param_name]: next }))}
                  min={spec.min}
                  max={spec.max}
                  step={spec.step}
                  unit={spec.unit}
                  disabled={loadingParams}
                />
              );
            })}
          </div>
        </CardContent>
      </Card>

      {grouped.map(({ category, items }) => {
        // Don't duplicate risk section
        const filtered = items.filter((r) => {
          const n = r.param_name.toLowerCase();
          return !(n.startsWith("sl_") || n.startsWith("tp_"));
        });
        if (filtered.length === 0) return null;

        return (
          <Card key={category}>
            <CardHeader>
              <CardTitle className="capitalize">{category}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              {filtered.map((r) => {
                const spec = inferFieldSpec(r.param_name);
                const current = Number(values[r.param_name] ?? r.param_value);

                if (spec.kind === "toggle") {
                  const checked = current >= 0.5;
                  return (
                    <div key={r.param_name} className="flex items-start justify-between gap-4">
                      <div className="space-y-0.5">
                        <p className="text-sm font-medium">{r.description || titleFromParamName(r.param_name)}</p>
                        <p className="text-xs text-muted-foreground">{r.param_name}</p>
                      </div>
                      <Switch
                        checked={checked}
                        disabled={loadingParams}
                        onCheckedChange={(next) => setValues((p) => ({ ...p, [r.param_name]: next ? 1 : 0 }))}
                      />
                    </div>
                  );
                }

                return (
                  <ParamNumberSliderField
                    key={r.param_name}
                    id={`param-${r.param_name}`}
                    label={r.description || titleFromParamName(r.param_name)}
                    description={r.param_name}
                    value={current}
                    onChange={(next) => setValues((p) => ({ ...p, [r.param_name]: next }))}
                    min={spec.min}
                    max={spec.max}
                    step={spec.step}
                    unit={spec.unit}
                    disabled={loadingParams}
                  />
                );
              })}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
